
import Vue from "vue";
import dayjs from "dayjs";

import TableCustom from "@/components/TableCustom.vue";
import FaultEventAlarmAcknowledge from "./FaultEventAlarmAcknowledge.vue";
import FaultEventAlarmDone from "./FaultEventAlarmDone.vue";
import FaultEventAlarmReject from "./FaultEventAlarmReject.vue";
import FaultEventAlarmDetails from "./FaultEventAlarmDetails.vue";
import FaultEventAlarmBulkAction from "./FaultEventAlarmBulkAction.vue";
import { FAULT_EVENT_PAGE_SIZES, MAX_BULK_FAULT_EVENTS } from "@/features/model/constants/faultEventAlarm";

type TableKind = "active" | "history";

interface TableState {
  items: any[];
  total: number;
  counts: { all: number; alarm: number; warning: number };
  page: number;
  size: number;
  loading: boolean;
  seq: number;
}

interface FilterOptions {
  sensors: { sensorId: string; sensorName: string }[];
  acked_by: string[];
  done_by: string[];
}

const emptyCounts = () => ({ all: 0, alarm: 0, warning: 0 });

const emptyTableState = (): TableState => ({
  items: [],
  total: 0,
  counts: emptyCounts(),
  page: 1,
  size: 10,
  loading: false,
  seq: 0,
});

export default Vue.extend({
  name: "FaultEventAlarm",
  components: {
    TableCustom,
    FaultEventAlarmAcknowledge,
    FaultEventAlarmDone,
    FaultEventAlarmReject,
    FaultEventAlarmDetails,
    FaultEventAlarmBulkAction,
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    model() {
      return this.$store.getters.model;
    },
    getModelId() {
      return this.$store.getters.model?.id;
    },
    alarmTypeOptions(): { text: string; value: number }[] {
      return [
        { text: "Warning", value: 1 },
        { text: "Alarm", value: 2 },
      ];
    },
    newDatas(): any[] {
      return (this as any).active.items;
    },
    historyDatas(): any[] {
      return (this as any).history.items;
    },
    isLoading(): boolean {
      return (this as any).active.loading || (this as any).history.loading;
    },
    filterParams(): Record<string, any> {
      const { filterSensorId, filterAlarmType, filterAckedBy, filterDoneBy } = this as any;
      return {
        sensor_id: filterSensorId || undefined,
        alarm_type: filterAlarmType || undefined,
        acked_by: filterAckedBy || undefined,
        done_by: filterDoneBy || undefined,
      };
    },
    selectedItems(): any[] {
      const ids: number[] = (this as any).selectedIds;
      return (this as any).newDatas.filter((p: any) => ids.includes(p.id));
    },
    selectedAlarmType(): number | null {
      const items: any[] = (this as any).selectedItems;
      if (!items.length) return null;
      return items[0].alarmType;
    },
    canSelectAll(): boolean {
      const datas: any[] = (this as any).currentPageItems;
      if (!datas.length) return false;
      const types = new Set(datas.map((p: any) => p.alarmType));
      return types.size === 1;
    },
    isAllNewSelected(): boolean {
      const datas: any[] = (this as any).currentPageItems;
      const ids: number[] = (this as any).selectedIds;
      return datas.length > 0 && datas.every((p: any) => ids.includes(p.id));
    },
    canBulkActionAlarm(): boolean {
      const items: any[] = (this as any).selectedItems;
      return items.length > 0 && items.every((i: any) => i.isAcknowledged && !i.isDone && i.alarmType === 2);
    },
    bulkDoneTooltip(): string {
      const items: any[] = (this as any).selectedItems;
      if (!items.length) return "Select alarm events first";
      return "All selected events must be acknowledged before marking as reject/approve";
    },
  },
  data() {
    const newHeaders = [
      { text: "", value: "select", sortable: false, width: "40px" },
      { text: "Date", value: "dateTime", sortable: true },
      { text: "Sensor Name", value: "sensorName", sortable: true },
      { text: "Alarm Message", value: "alarmMessage", style: "width: 50%;", sortable: true },
      { text: "Alarm Type", value: "alarmType", sortable: true },
      { text: "Alarm Value", value: "alarmValue", sortable: true },
      { text: "Ack", value: "acknowledge", sortable: false, textAlign: "center", width: "90px" },
      { text: "Action", value: "reject-done", sortable: false, textAlign: "center", width: "90px" },
      { text: "Details", value: "details", sortable: false, textAlign: "center", width: "80px" },
    ];
    const historyHeaders = [
      { text: "Date", value: "dateTime", sortable: true },
      { text: "Sensor Name", value: "sensorName", sortable: true },
      { text: "Alarm Message", value: "alarmMessage", style: "width: 50%;", sortable: true },
      { text: "Alarm Type", value: "alarmType", sortable: true },
      { text: "Alarm Value", value: "alarmValue", sortable: true },
      { text: "Update at", value: "UpdateAt", sortable: false },
      { text: "Details", value: "details", sortable: false, textAlign: "center", width: "80px" },
    ];

    const members: any[] = [];

    return {
      newHeaders,
      historyHeaders,
      active: emptyTableState(),
      history: emptyTableState(),
      filterOptions: { sensors: [], acked_by: [], done_by: [] } as FilterOptions,
      refreshPending: false,
      members,
      isActiveTypeNewAlarm: "All",
      isActiveTypeAlarmHistory: "All",
      filterSensorId: null as string | null,
      filterAlarmType: null as number | null,
      filterAckedBy: null as string | null,
      filterDoneBy: null as string | null,
      selectedIds: [] as number[],
      currentPageItems: [] as any[],
      pageSizes: FAULT_EVENT_PAGE_SIZES,
    };
  },
  async created() {
    await Promise.all([(this as any).refreshAll(), (this as any).fetchFilterOptions()]);
    await (this as any).fetchMembers();
  },
  methods: {
    tableState(kind: TableKind): TableState {
      return kind === "active" ? (this as any).active : (this as any).history;
    },
    tableRef(kind: TableKind): any {
      return kind === "active" ? this.$refs.newTable : this.$refs.historyTable;
    },
    async fetchTable(kind: TableKind) {
      const modelId = this.model?.id;
      if (!modelId) return;

      const state: TableState = (this as any).tableState(kind);
      const seq = ++state.seq;
      state.loading = true;
      try {
        const result = await (this as any).$dep.modelUseCase.getFaultEventPage(modelId, kind, {
          page: state.page,
          size: state.size,
          ...(this as any).filterParams,
        });
        if (seq !== state.seq) return; // a newer request superseded this one

        // the current page emptied (e.g. after acking all of it) → step back to the last page
        if (!result.items.length && state.page > 1 && result.total > 0) {
          (this as any).tableRef(kind)?.setPage(Math.ceil(result.total / state.size));
          return;
        }
        state.items = result.items;
        state.total = result.total;
        state.counts = result.counts;
      } catch (error) {
        if (seq !== state.seq) return;
        state.items = [];
        state.total = 0;
        state.counts = emptyCounts();
      } finally {
        if (seq === state.seq) state.loading = false;
      }
    },
    async fetchFilterOptions() {
      const modelId = this.model?.id;
      if (!modelId) return;
      try {
        (this as any).filterOptions = await (this as any).$dep.modelUseCase.getFaultEventFilterOptions(modelId);
      } catch (error) {
        (this as any).filterOptions = { sensors: [], acked_by: [], done_by: [] };
      }
    },
    // Back to page 1 on both tables and refetch them.
    async refreshAll() {
      (this as any).selectedIds = [];
      for (const kind of ["active", "history"] as TableKind[]) {
        (this as any).tableState(kind).page = 1;
        (this as any).tableRef(kind)?.resetPage();
      }
      await Promise.all([(this as any).fetchTable("active"), (this as any).fetchTable("history")]);
    },
    // Coalesce several triggers in the same tick (e.g. Refresh clearing all filters) into one refresh.
    scheduleRefreshAll() {
      if ((this as any).refreshPending) return;
      (this as any).refreshPending = true;
      this.$nextTick(async () => {
        (this as any).refreshPending = false;
        await (this as any).refreshAll();
      });
    },
    onTablePagination(kind: TableKind, { page, itemsPerPage }: { page: number; itemsPerPage: number }) {
      const state: TableState = (this as any).tableState(kind);
      if (state.page === page && state.size === itemsPerPage) return;
      state.page = page;
      state.size = itemsPerPage;
      if (kind === "active") (this as any).selectedIds = [];
      (this as any).fetchTable(kind);
    },
    async fetchMembers() {
      try {
        const { id } = this.company;
        if (!id) {
          console.warn("FetchMembers skipped: company id not ready");
          return;
        }

        const members = await (this as any).$dep.memberUseCase.getMembers(id);
        (this as any).members = members.items;
      } catch (error) {
        (this as any).members = [];
      }
    },
    onSelectType(status: number) {
      (this as any).filterAlarmType = status === 0 ? null : status;
    },
    onSelectAll() {
      (this as any).filterSensorId = null;
      (this as any).filterAlarmType = null;
      (this as any).filterAckedBy = null;
      (this as any).filterDoneBy = null;
      (this as any).fetchFilterOptions();
      (this as any).scheduleRefreshAll();
    },
    // After an ack / approve / reject: keep the current pages, refetch both tables.
    async reload() {
      (this as any).selectedIds = [];
      await Promise.all([
        (this as any).fetchTable("active"),
        (this as any).fetchTable("history"),
        (this as any).fetchFilterOptions(),
      ]);
      await this.$store.dispatch("getModelViewData");
      this.$emit("reload");
    },
    async onDownloadExcel() {
      const { id } = this.model;
      const url = `${process.env.VUE_APP_API_BASE_URL}/api/v1/fault-eventdownload-fault-event?model_id=${id}`;

      window.open(url);
    },
    onToggleRow(item: any) {
      const ids: number[] = (this as any).selectedIds;
      const idx = ids.indexOf(item.id);
      if (idx === -1) {
        if (ids.length >= MAX_BULK_FAULT_EVENTS) {
          (this as any).$store.dispatch("openAlertMessage", {
            open: true,
            message: `You can select up to ${MAX_BULK_FAULT_EVENTS} events at a time.`,
            type: "warning",
          });
          return;
        }
        (this as any).selectedIds = [...ids, item.id];
      } else {
        (this as any).selectedIds = ids.filter((id) => id !== item.id);
      }
    },
    onToggleSelectAll(val: boolean) {
      if (val) {
        (this as any).selectedIds = (this as any).currentPageItems.slice(0, MAX_BULK_FAULT_EVENTS).map((p: any) => p.id);
      } else {
        (this as any).selectedIds = [];
      }
    },
    openBulkAck() {
      ((this as any).$refs.bulkAckDialog as any).open();
    },
    openBulkDone() {
      ((this as any).$refs.bulkDoneDialog as any).open();
    },
    openBulkReject() {
      ((this as any).$refs.bulkRejectDialog as any).open();
    },
    async onBulkReload() {
      await (this as any).reload();
    },
    formatDate(date: any, format = "DD MMM YYYY, HH:mm") {
      return dayjs(date).format(format);
    },
  },
  watch: {
    async getModelId() {
      (this as any).fetchFilterOptions();
      (this as any).scheduleRefreshAll();
      await (this as any).fetchMembers();
    },
    filterParams() {
      (this as any).scheduleRefreshAll();
    },
    // when company.id becomes available → fetch members again
    "company.id": {
      immediate: false,
      async handler(newId) {
        if (newId) {
          await (this as any).fetchMembers();
        }
      },
    },
  },
});
